import {
  Button,
  MarketCapitalizationSection,
  ReturnSection,
  Section,
  Text,
  YieldBearingAssetTvlAndReturnCarouselSection,
} from '@/components';
import { useYieldBearingAssetRawData } from '@/components/providers';
import { YieldBearingAssetInterface } from '@/interfaces';
import { calculateAverageYield } from '@/utils/functionalUtils';
import { LABELS } from '@/utils/strings';
import { FC, memo, useEffect, useState } from 'react';
interface YieldBearingAssetAboutSectionProps {
  asset: YieldBearingAssetInterface;
  showShortDescription?: boolean;
  showHistoricalPerformance?: boolean;
  onBrochureHelpClick?: (
    markdown: string,
    asset: YieldBearingAssetInterface,
  ) => void;
}
const YieldBearingAssetAboutSection: FC<YieldBearingAssetAboutSectionProps> =
  memo(
    ({
      asset,
      showShortDescription = true,
      showHistoricalPerformance = false,
      onBrochureHelpClick,
    }) => {
      const { fetchAssetHistoricalData } = useYieldBearingAssetRawData();
      const [historicalData, setHistoricalData] = useState<any[] | null>(null);
      useEffect(() => {
        const fetchData = async () => {
          const data = await fetchAssetHistoricalData(asset.ticker);
          setHistoricalData(data);
        };
        fetchData();
      }, [asset.ticker, fetchAssetHistoricalData]);
      const ytdYield = historicalData
        ? calculateAverageYield(historicalData, 'ytd')
        : null;
      const yield30d = historicalData
        ? calculateAverageYield(historicalData, 30)
        : null;
      const yield60d = historicalData
        ? calculateAverageYield(historicalData, 60)
        : null;
      const yield90d = historicalData
        ? calculateAverageYield(historicalData, 90)
        : null;
      const historicalYieldComponents = [
        <ReturnSection
          key="ytd"
          label={
            LABELS.YIELD_BEARING_ASSET_DETAIL_CARD.HISTORICAL_YIELD_LABELS.YTD
          }
          returnRate={ytdYield}
        />,
        <ReturnSection
          key="30d"
          label={
            LABELS.YIELD_BEARING_ASSET_DETAIL_CARD.HISTORICAL_YIELD_LABELS.D30
          }
          returnRate={yield30d}
        />,
        <ReturnSection
          key="60d"
          label={
            LABELS.YIELD_BEARING_ASSET_DETAIL_CARD.HISTORICAL_YIELD_LABELS.D60
          }
          returnRate={yield60d}
        />,
        <ReturnSection
          key="90d"
          label={
            LABELS.YIELD_BEARING_ASSET_DETAIL_CARD.HISTORICAL_YIELD_LABELS.D90
          }
          returnRate={yield90d}
        />,
      ].filter((c) => c.props.returnRate !== null);
      const totalItems = 2 + historicalYieldComponents.length;
      const renderGrid = () => {
        const staticComponents = [
          <MarketCapitalizationSection key="mcap" asset={asset} />,
          <ReturnSection
            key="apy"
            label={LABELS.YIELD_BEARING_ASSET_DETAIL_CARD.RETURN_METRIC_LABEL}
            returnRate={asset.apy}
          />,
        ];
        if (totalItems === 5) {
          return (
            <div className="flex flex-col gap-sm">
              <div className="grid grid-cols-2 gap-sm">{staticComponents}</div>
              <div className="grid grid-cols-3 gap-sm">
                {historicalYieldComponents}
              </div>
            </div>
          );
        }
        const getGridClass = () => {
          switch (totalItems) {
            case 2:
              return 'grid-cols-2';
            case 3:
              return 'grid-cols-3';
            case 4:
              return 'grid-cols-2';
            case 6:
              return 'grid-cols-3';
            default:
              return 'grid-cols-3';
          }
        };
        return (
          <div className={`grid ${getGridClass()} gap-sm`}>
            {staticComponents}
            {historicalYieldComponents}
          </div>
        );
      };
      return (
        <Section
          headerTitle="About"
          body={
            <div className="flex flex-col gap-sm">
              {showShortDescription ? (
                <Text value={asset.shortDescription} />
              ) : (
                <Text value={asset.longDescription} />
              )}
              {renderGrid()}
              {showHistoricalPerformance && (
                <YieldBearingAssetTvlAndReturnCarouselSection asset={asset} />
              )}
              {asset.brochureInMarkdown && onBrochureHelpClick && (
                <Button
                  icon={'ai'}
                  intent="ai"
                  iconStyles={{ intent: 'ai', size: 'md' }}
                  className="w-full"
                  onClick={() =>
                    onBrochureHelpClick(asset.brochureInMarkdown || '', asset)
                  }
                >
                  Research
                </Button>
              )}
            </div>
          }
        />
      );
    },
  );
export default YieldBearingAssetAboutSection;
